// The "why did they get this score?" modal — the receipts behind one matrix
// cell or grades-table row. Shows every grade the pair (student × specific
// skill) has, newest attempt first: score, the grader's rationale, the
// evidence snippets quoted from the student's work, and the not-assessed
// reason when the artifact couldn't show the skill. Purely presentational:
// hosts fetch the rows (useProjectGrades) and decide which links/actions to
// offer. Shared by the class-overview heatmap and the grades page.
import Link from '../../lib/Link';
import { ExternalLink, FileText, Image as ImageIcon, Quote } from '../../lib/icons';
import { Badge, Button, Modal, ScoreChip, Skeleton } from '../ds';
import AssetThumb from './AssetThumb';
import RubricLadder, { levelDescFor } from './RubricLadder';
import ScoreTally from './ScoreTally';

function fmtDay(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

// Evidence arrives as legacy strings or M1 objects {kind, asset_id, text}.
// Normalize both so the render can honor the honesty rule (media-ux U·5):
// a QUOTE is the student's verbatim text; an image item is the grader's
// OBSERVATION — italic, labeled "seen in photo", never quote-marked.
function snippetsOf(grade) {
  const e = grade?.evidence_snippets;
  const list = Array.isArray(e) ? e : typeof e === 'string' && e.trim() ? [e] : [];
  return list
    .map((s) => {
      if (typeof s === 'string') return s.trim() ? { kind: 'text', text: s } : null;
      if (s && typeof s === 'object' && s.text) {
        return {
          kind: s.kind === 'image' ? 'image' : 'text',
          asset_id: s.asset_id ?? null,
          text: String(s.text),
        };
      }
      return null;
    })
    .filter(Boolean);
}

function GradeEntry({ grade, showFile, onOpenWork }) {
  const snippets = snippetsOf(grade);
  const score = grade.score_1_5 == null ? null : Number(grade.score_1_5);
  // The verdict in the rubric's own words — a score never floats context-free.
  const levelText = grade.assessable ? levelDescFor(grade.rubric_used, score) : null;

  return (
    <div className="rounded-xl border border-border bg-secondary/25 p-4">
      {/* Cross-project context (score anatomy rows carry it; single-project
          hosts simply don't send these fields). */}
      {grade.project_title && (
        <div className="mb-1.5 text-[11px] text-muted-foreground">
          <strong className="font-semibold text-foreground">{grade.project_title}</strong>
          {grade.grade_label && <> · grade {grade.grade_label}</>}
          {grade.worked_at && <> · {fmtDay(grade.worked_at)}</>}
        </div>
      )}
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <ScoreChip score={score} />
        {levelText && (
          <span className="min-w-0 flex-1 text-[12.5px] font-medium leading-snug text-foreground/85">
            {levelText}
          </span>
        )}
        {grade.attempt_no > 1 && <Badge tone="outline">Attempt {grade.attempt_no}</Badge>}
        {!grade.assessable && <Badge tone="warning">Not assessed</Badge>}
        {/* Anatomy rows span projects, so the viewer link lives PER ENTRY —
            each grade opens its own project's container. */}
        {onOpenWork && (
          <button
            type="button"
            onClick={() => onOpenWork()}
            title="Open this submission in the work viewer"
            className="ml-auto inline-flex shrink-0 items-center gap-1 text-[11px] font-semibold text-primary hover:underline"
          >
            <FileText className="size-3" aria-hidden="true" />
            Open the work
          </button>
        )}
      </div>
      {showFile && grade.file_name && (
        <div className="mb-2 flex min-w-0 items-center gap-1.5 text-[11px] text-muted-foreground">
          <FileText className="size-3 shrink-0" />
          <span className="truncate" title={grade.file_name}>
            {grade.file_name}
          </span>
        </div>
      )}

      {!grade.assessable && grade.not_assessed_reason && (
        <p className="mb-2 text-[13px] leading-relaxed text-muted-foreground">
          <strong className="font-semibold text-foreground">Why not assessed: </strong>
          {grade.not_assessed_reason}
        </p>
      )}

      {grade.rationale && (
        <div className="mt-1">
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Why this score
          </div>
          <p className="text-[13px] leading-relaxed text-foreground">{grade.rationale}</p>
        </div>
      )}

      {/* The receipts. The student's words are the POINT of this surface, so
          they wear foreground ink at reading size — the chrome around them
          stays quiet. Each receipt walks back to the work: text quotes land
          highlighted in the extraction, photo observations land on their
          photo section with the cited region emphasized. */}
      {snippets.length > 0 && (
        <div className="mt-3 flex flex-col gap-2">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Evidence from the work · {snippets.length} excerpt{snippets.length === 1 ? '' : 's'}
          </div>
          {snippets.map((snip, i) => {
            const openAt = onOpenWork
              ? () =>
                  onOpenWork({
                    assetId: snip.kind === 'image' ? (snip.asset_id ?? null) : null,
                    highlight: snip.kind === 'text' ? snip.text : null,
                  })
              : null;
            return snip.kind === 'image' ? (
              <div
                key={i}
                className="rounded-r-lg border-l-2 border-border bg-card px-3 py-2.5"
              >
                <div className="flex gap-2.5">
                  {snip.asset_id != null ? (
                    <AssetThumb
                      asset={{ id: snip.asset_id, kind: 'image', status: 'understood', original_name: null }}
                      className="h-12 w-16 shrink-0"
                    />
                  ) : (
                    <ImageIcon className="mt-0.5 size-3 shrink-0 text-primary/50" aria-hidden="true" />
                  )}
                  <em className="text-[13px] leading-relaxed text-foreground/90">{snip.text}</em>
                </div>
                <div className="mt-1.5 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/80">
                  seen in photo
                  {openAt && (
                    <button
                      type="button"
                      onClick={openAt}
                      className="normal-case tracking-normal text-[11px] font-semibold text-primary hover:underline"
                    >
                      Show in work →
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <blockquote
                key={i}
                className="rounded-r-lg border-l-2 border-primary/50 bg-card px-3 py-2.5"
              >
                <div className="flex gap-2">
                  <Quote className="mt-0.5 size-3.5 shrink-0 text-primary/40" aria-hidden="true" />
                  <span className="text-[13.5px] leading-relaxed text-foreground">{snip.text}</span>
                </div>
                <div className="mt-1.5 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/80">
                  the student&apos;s own words
                  {openAt && (
                    <button
                      type="button"
                      onClick={openAt}
                      className="normal-case tracking-normal text-[11px] font-semibold text-primary hover:underline"
                    >
                      Show in work →
                    </button>
                  )}
                </div>
              </blockquote>
            );
          })}
        </div>
      )}

      {grade.assessable && !grade.rationale && snippets.length === 0 && (
        <p className="text-[12.5px] italic text-muted-foreground">
          No rationale or evidence was recorded for this grade (older grading runs didn&apos;t
          save them — re-grading the project will fill these in).
        </p>
      )}

      {/* The rubric this grade was judged under (media-ux §4) — on demand,
          with the achieved level lit. */}
      {grade.assessable && score != null && (
        <details className="mt-3">
          <summary className="cursor-pointer select-none text-[11px] font-semibold text-muted-foreground hover:text-foreground">
            What a {score} means here
          </summary>
          <div className="mt-2">
            <RubricLadder rubric={grade.rubric_used} score={score} />
          </div>
        </details>
      )}
    </div>
  );
}

export default function GradeDetailModal({
  open,
  onClose,
  studentName,
  skillName,
  breadcrumb, // { area, competency, skill } | null
  grades, // GradeRow[] for this student × specific skill, newest attempt first
  loading = false,
  anatomy = false, // score anatomy (media-ux §5): tally the medians' inputs
  gradesHref, // deep link into the Grades page, pre-filtered (optional)
  discHref, // the student's disc page (optional)
  onOpenSubmission, // opens the SubmissionViewer on the host page (optional)
  // (grade, anchor?) => void: per-entry viewer open. anchor = { assetId,
  // highlight } when a specific receipt was clicked — the host passes it to
  // SubmissionViewer so the teacher lands ON the cited passage/photo.
  onOpenWork,
}) {
  const tallyScores = anatomy
    ? grades.filter((g) => g.assessable && g.score_1_5 != null).map((g) => Number(g.score_1_5))
    : [];
  const crumb = breadcrumb
    ? [breadcrumb.area, breadcrumb.competency, breadcrumb.skill].filter(Boolean).join(' › ')
    : null;

  return (
    <Modal open={open} onClose={onClose} labelledBy="gd-title" className="max-w-2xl">
      <div className="flex max-h-[80vh] flex-col">
        <div className="border-b border-border px-5 pb-3 pt-4">
          <h3 id="gd-title" className="text-[15px] font-bold text-foreground">
            {studentName} · {skillName}
          </h3>
          {crumb && <div className="mt-0.5 text-[11px] text-muted-foreground">{crumb}</div>}
        </div>

        <div className="flex flex-col gap-3 overflow-y-auto px-5 py-4">
          {loading ? (
            <>
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
            </>
          ) : grades.length === 0 ? (
            <p className="py-4 text-center text-[13px] text-muted-foreground">
              No grade detail found for this student and skill developmental descriptor.
            </p>
          ) : (
            <>
              {anatomy && tallyScores.length > 0 && (
                <div className="rounded-xl border border-border bg-card p-3">
                  <ScoreTally scores={tallyScores} />
                </div>
              )}
              {grades.map((g, i) => (
                <GradeEntry
                  key={`${g.submission_id}-${g.specific_skill_id ?? g.grade_id ?? i}-${i}`}
                  grade={g}
                  showFile
                  onOpenWork={onOpenWork ? (anchor) => onOpenWork(g, anchor ?? null) : undefined}
                />
              ))}
            </>
          )}
        </div>

        {(onOpenSubmission || gradesHref || discHref) && (
          <div className="flex flex-wrap items-center gap-2 border-t border-border px-5 py-3">
            {onOpenSubmission && (
              <Button size="sm" variant="outline" onClick={onOpenSubmission}>
                <FileText className="size-3.5" />
                Open submission
              </Button>
            )}
            {gradesHref && (
              <Link
                href={gradesHref}
                className="inline-flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
              >
                <ExternalLink className="size-3.5" />
                All grades for this filter
              </Link>
            )}
            {discHref && (
              <Link
                href={discHref}
                className="inline-flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
              >
                <ExternalLink className="size-3.5" />
                Student disc
              </Link>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
